<script lang="ts">
  import Chart from "chart.js";
  import { addHours, format, startOfToday, isAfter } from "date-fns";
  import { utcToZonedTime } from "date-fns-tz";
  import Box from "@cohesionib/biblioteca/lib/Box";

  import { browserTimeZone } from "../../util/general";
  import { getAppContext } from "../../AppContext";
  import type { RGB } from "@cohesionib/biblioteca/lib/HexTheme";
  import type { GetOccupancySensorsQuery } from "../../frontendGatewaySdk";

  type TotalOccupancyByBucket = NonNullable<
    NonNullable<
      GetOccupancySensorsQuery["building"]
    >["occupancyBySensor"]
  >;

  export let data: TotalOccupancyByBucket;

  const dataWindowStart = addHours(startOfToday(), 4);
  const dataWindowEnd = addHours(dataWindowStart, 15);
  const { themeAccentColorRGB, themeAccentColorHEX } = getAppContext();

  const formattedOccupancyData = data.reduce((prev, curr) => {
    if (!prev[curr.date]) {
      prev[curr.date] = {
        date: curr.date,
        value: curr.value
      };
    }
    prev[curr.date] = {
      ...prev[curr.date],
      value: prev[curr.date].value + curr.value
    };
    return prev;
  }, {});

  export let capacity: number;
  const groomedData: { x: Date; y: number }[] = Object.values(formattedOccupancyData)
    .map((d) => ({
      x: utcToZonedTime(new Date(d?.date || ""), browserTimeZone),
      y: Math.max(d?.value || 0, 0),
    }))
    .filter((d) => isAfter(d.x, dataWindowStart));

  const formatDate = (date: Date): string =>
    format(date, "h:mmbbbb").replaceAll(".", "").replace("noon", "pm");

  const rgbToRGBA = (color: RGB, opacity: number): string =>
    `rgba(${color.r}, ${color.g}, ${color.b}, ${opacity})`;

  const initGraph = (element: HTMLCanvasElement) => {
    new Chart(element, {
      type: "line",
      options: {
        legend: {
          display: false,
        },
        animation: {
          duration: 600,
        },
        scales: {
          yAxes: [
            {
              ticks: {
                suggestedMin: 0,
                suggestedMax: capacity,
              },
            },
          ],
        },
      },
      data: {
        labels: groomedData.map((d) => formatDate(d.x)),
        datasets: [
          {
            label: "Occupancy",
            data: groomedData.map((d) => d.y),
            pointBackgroundColor: themeAccentColorHEX,
            backgroundColor: rgbToRGBA(themeAccentColorRGB, 0.4),
            borderColor: rgbToRGBA(themeAccentColorRGB, 0.65),
          },
        ],
      },
    });
  };
</script>

<style>
  #chart-container {
    width: 100%;
    height: 35em;
    display: flex;
    justify-content: center;
    align-items: center;
    height: min-content;
  }
  #chart-wrapper {
    height: 100%;
    max-width: 98em;
    min-width: 20em;
    width: 100%;
  }
  #graph-title {
    color: #4e4e4e;
    font-size: 1.5em;
    font-weight: 300;
  }
  #graph-subtitle {
    font-weight: 200;
    color: #4e4e4e;
    font-size: 0.75em;
    padding-bottom: 1em;
  }
  #graph-content-wrapper {
    padding: 1.5em 2em 1.5em 2em;
  }
</style>

<Box title="Occupancy Trends">
  <div id="graph-content-wrapper">
    <div id="graph-title">Occupancy Trends</div>
    <div id="graph-subtitle">
      What does occupancy look like throughout the day?
    </div>
    <div id="chart-container">
      <div id="chart-wrapper"><canvas id="line-graph" use:initGraph /></div>
    </div>
  </div>
</Box>
